import {
  AdditiveBlending,
  BufferGeometry,
  BufferAttribute,
  Mesh,
  ShaderMaterial,
  Group,
  Vector3,
  CatmullRomCurve3,
  DoubleSide,
} from "three";
import {
  ArcsConfig,
  ArcState,
  ArcSpawnData,
  GlowLayer,
  DEFAULT_CONFIG,
} from "./types";

// Vertex shader for ribbon
const vertexShader = `
  attribute float aOpacity;
  varying float vOpacity;
  varying vec2 vUv;
  
  void main() {
    vOpacity = aOpacity;
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

// Fragment shader - soft edges on the ribbon
const fragmentShader = `
  varying float vOpacity;
  varying vec2 vUv;
  
  uniform vec3 uColor;
  uniform float uIntensity;
  
  void main() {
    // Soft edge falloff based on distance from center (v = 0.5 is center)
    float dist = abs(vUv.y - 0.5) * 2.0;
    float alpha = 1.0 - smoothstep(0.0, 1.0, dist);
    alpha = alpha * alpha; // Quadratic falloff
    
    gl_FragColor = vec4(uColor, alpha * vOpacity * uIntensity);
  }
`;

interface PooledLayer {
  mesh: Mesh;
  geometry: BufferGeometry;
  material: ShaderMaterial;
  positions: Float32Array;
  uvs: Float32Array;
  opacities: Float32Array;
  width: number;
  baseOpacity: number;
  isCore: boolean;
}

interface PooledArc {
  layers: PooledLayer[];
  active: boolean;
}

export interface ArcsOptions {
  /** Full or partial configuration (merged with defaults) */
  config?: Partial<ArcsConfig>;
  /** Force mobile mode regardless of screen size */
  forceMobile?: boolean;
  /** Custom mobile detection function */
  mobileDetector?: () => boolean;
  /** Global intensity multiplier (0-1) */
  intensityMultiplier?: number;
  /** Jaggedness multiplier for mobile (default: 0.6) */
  mobileJaggednessFactor?: number;
}

/**
 * Generic, configurable arc/lightning effect system.
 * Can be used for electric arcs, lightning bolts, energy beams, etc.
 */
export class Arcs extends Group {
  private arcs: ArcState[] = [];
  private config: ArcsConfig;
  private isMobile: boolean;
  private glowLayers: GlowLayer[];
  private arcPool: PooledArc[] = [];
  private globalIntensity = 1;
  private intensityMultiplier: number;
  private mobileJaggednessFactor: number;

  // For manual arc management (no auto-spawning)
  private manualMode = false;

  constructor(options: ArcsOptions = {}) {
    super();

    // Merge config with defaults
    this.config = this.mergeConfig(options.config);
    this.intensityMultiplier = options.intensityMultiplier ?? 1;
    this.mobileJaggednessFactor = options.mobileJaggednessFactor ?? 0.6;

    // Detect mobile
    if (options.forceMobile !== undefined) {
      this.isMobile = options.forceMobile;
    } else if (options.mobileDetector) {
      this.isMobile = options.mobileDetector();
    } else {
      this.isMobile = window.innerWidth < 768;
    }

    this.frustumCulled = false;
    this.glowLayers = this.isMobile
      ? this.config.style.glowLayersMobile
      : this.config.style.glowLayers;

    this.initializePool();
  }

  private mergeConfig(partial?: Partial<ArcsConfig>): ArcsConfig {
    if (!partial) return { ...DEFAULT_CONFIG };

    return {
      animation: { ...DEFAULT_CONFIG.animation, ...partial.animation },
      spawn: { ...DEFAULT_CONFIG.spawn, ...partial.spawn },
      style: { ...DEFAULT_CONFIG.style, ...partial.style },
    };
  }

  private initializePool(): void {
    const numSegments = this.config.animation.curveSegments;
    const verticesPerRibbon = (numSegments + 1) * 2;
    const maxArcs = this.config.spawn.maxArcs;

    for (let arcIndex = 0; arcIndex < maxArcs; arcIndex++) {
      const layers: PooledLayer[] = [];

      for (const [width, baseOpacity, isCore] of this.glowLayers) {
        const geometry = new BufferGeometry();

        const positions = new Float32Array(verticesPerRibbon * 3);
        const uvs = new Float32Array(verticesPerRibbon * 2);
        const opacities = new Float32Array(verticesPerRibbon);

        // Initialize UVs (these don't change)
        for (let i = 0; i <= numSegments; i++) {
          const idx = i * 2;
          uvs[idx * 2] = i / numSegments;
          uvs[idx * 2 + 1] = 0;
          uvs[(idx + 1) * 2] = i / numSegments;
          uvs[(idx + 1) * 2 + 1] = 1;
        }

        geometry.setAttribute("position", new BufferAttribute(positions, 3));
        geometry.setAttribute("uv", new BufferAttribute(uvs, 2));
        geometry.setAttribute("aOpacity", new BufferAttribute(opacities, 1));

        // Create index buffer for triangle strip
        const indices: number[] = [];
        for (let i = 0; i < numSegments; i++) {
          const idx = i * 2;
          indices.push(idx, idx + 1, idx + 2);
          indices.push(idx + 1, idx + 3, idx + 2);
        }
        geometry.setIndex(indices);

        // Core is white, glow layers use configured color
        const color = isCore
          ? new Vector3(
              this.config.style.coreColor.r,
              this.config.style.coreColor.g,
              this.config.style.coreColor.b,
            )
          : new Vector3(
              this.config.style.glowColor.r,
              this.config.style.glowColor.g,
              this.config.style.glowColor.b,
            );

        const material = new ShaderMaterial({
          uniforms: {
            uColor: { value: color },
            uIntensity: { value: 1.0 },
          },
          vertexShader,
          fragmentShader,
          transparent: true,
          depthWrite: false,
          blending: AdditiveBlending,
          side: DoubleSide,
        });

        const mesh = new Mesh(geometry, material);
        mesh.visible = false;
        mesh.frustumCulled = false;

        this.add(mesh);
        layers.push({
          mesh,
          geometry,
          material,
          positions,
          uvs,
          opacities,
          width,
          baseOpacity,
          isCore,
        });
      }

      this.arcPool.push({ layers, active: false });
    }
  }

  /**
   * Set global intensity (e.g., for fade effects)
   */
  public setIntensity(intensity: number): void {
    this.globalIntensity = Math.max(0, Math.min(1, intensity));
  }

  /**
   * Enable manual mode (disables auto-spawning in updateAuto)
   */
  public setManualMode(enabled: boolean): void {
    this.manualMode = enabled;
  }

  /**
   * Spawn an arc manually with explicit start/end points
   */
  public spawnArc(data: ArcSpawnData): boolean {
    if (this.arcs.length >= this.config.spawn.maxArcs) return false;

    const { animation, spawn } = this.config;
    const numSegments =
      animation.segmentsMin +
      Math.floor(Math.random() * animation.segmentsRange);

    const horizontalCurrent: number[] = [];
    const horizontalTarget: number[] = [];
    const verticalCurrent: number[] = [];
    const verticalTarget: number[] = [];

    for (let i = 0; i < numSegments; i++) {
      const hVal = (Math.random() - 0.5) * 2 * animation.horizontalRange;
      const vVal = (Math.random() - 0.5) * 2 * animation.verticalRange;
      horizontalCurrent.push(hVal);
      horizontalTarget.push(hVal);
      verticalCurrent.push(vVal);
      verticalTarget.push(vVal);
    }

    const [minLife, maxLife] = spawn.lifetime;
    const lifetime =
      data.lifetime ?? minLife + Math.random() * (maxLife - minLife);

    this.arcs.push({
      startPoint: data.startPoint.clone(),
      endPoint: data.endPoint.clone(),
      progress: 0,
      lifetime,
      baseIntensity: data.intensity ?? 0.8,
      horizontalCurrent,
      horizontalTarget,
      verticalCurrent,
      verticalTarget,
    });

    return true;
  }

  /**
   * Update an existing arc's endpoints (for moving arcs)
   */
  public updateArcEndpoints(index: number, start: Vector3, end: Vector3): void {
    const arc = this.arcs[index];
    if (arc) {
      arc.startPoint.copy(start);
      arc.endPoint.copy(end);
    }
  }

  /**
   * Get current arc count
   */
  public getArcCount(): number {
    return this.arcs.length;
  }

  /**
   * Clear all arcs
   */
  public clearArcs(): void {
    this.arcs = [];
    for (const pooledArc of this.arcPool) {
      this.hideArc(pooledArc);
    }
  }

  private generateArcCurve(arc: ArcState): Vector3[] {
    const {
      startPoint: start,
      endPoint: end,
      horizontalCurrent,
      horizontalTarget,
      verticalCurrent,
      verticalTarget,
    } = arc;

    const { animation } = this.config;
    const segments = horizontalCurrent.length;
    const controlPoints: Vector3[] = [start.clone()];
    const direction = end.clone().sub(start);
    const length = direction.length();
    direction.normalize();

    const perp1 = new Vector3(
      Math.abs(direction.x) > 0.9 ? 0 : 1,
      Math.abs(direction.x) > 0.9 ? 1 : 0,
      0,
    )
      .cross(direction)
      .normalize();
    const perp2 = direction.clone().cross(perp1).normalize();

    const jaggedMultiplier = this.isMobile ? this.mobileJaggednessFactor : 1.0;
    const multiplier = jaggedMultiplier * animation.jaggedness;

    for (let i = 1; i < segments; i++) {
      const t = i / segments;
      const middleFactor = Math.pow(Math.sin(t * Math.PI), 2);
      const displacement = middleFactor * multiplier;

      if (Math.random() < animation.targetChangeChance) {
        horizontalTarget[i] =
          (Math.random() - 0.5) * 2 * animation.horizontalRange;
      }
      if (Math.random() < animation.targetChangeChance * 0.5) {
        verticalTarget[i] = (Math.random() - 0.5) * 2 * animation.verticalRange;
      }

      const hCurrent = horizontalCurrent[i] ?? 0;
      const hTarget = horizontalTarget[i] ?? 0;
      const vCurrent = verticalCurrent[i] ?? 0;
      const vTarget = verticalTarget[i] ?? 0;

      horizontalCurrent[i] =
        hCurrent + (hTarget - hCurrent) * animation.horizontalEasing;
      verticalCurrent[i] =
        vCurrent + (vTarget - vCurrent) * animation.verticalEasing;

      const horizontalOffset = (horizontalCurrent[i] ?? 0) * displacement;
      const verticalOffset = (verticalCurrent[i] ?? 0) * displacement;

      controlPoints.push(
        start
          .clone()
          .addScaledVector(direction, length * t)
          .addScaledVector(perp1, horizontalOffset)
          .addScaledVector(perp2, verticalOffset),
      );
    }

    controlPoints.push(end.clone());

    const curve = new CatmullRomCurve3(controlPoints);
    return curve.getPoints(animation.curveSegments);
  }

  private updatePooledArc(
    pooledArc: PooledArc,
    curvePoints: Vector3[],
    intensity: number,
    arcLength: number,
  ): void {
    const tangents: Vector3[] = [];
    const normals: Vector3[] = [];

    for (let i = 0; i < curvePoints.length; i++) {
      const prev = curvePoints[Math.max(0, i - 1)]!;
      const next = curvePoints[Math.min(curvePoints.length - 1, i + 1)]!;
      const tangent = new Vector3().subVectors(next, prev).normalize();
      tangents.push(tangent);

      const up = new Vector3(0, 1, 0);
      const normal = new Vector3().crossVectors(tangent, up).normalize();
      if (normal.length() < 0.1) {
        normal.set(1, 0, 0);
      }
      normals.push(normal);
    }

    for (const layer of pooledArc.layers) {
      const {
        positions,
        opacities,
        geometry,
        material,
        mesh,
        width,
        baseOpacity,
      } = layer;

      const halfWidth = (width * arcLength) / 2;

      for (let i = 0; i < curvePoints.length; i++) {
        const p = curvePoints[i]!;
        const normal = normals[i]!;
        const idx = i * 2;

        const t = i / (curvePoints.length - 1);
        const middleFactor = Math.sin(t * Math.PI);
        const adjustedHalfWidth = halfWidth * (0.3 + middleFactor * 0.7);

        positions[idx * 3] = p.x + normal.x * adjustedHalfWidth;
        positions[idx * 3 + 1] = p.y + normal.y * adjustedHalfWidth;
        positions[idx * 3 + 2] = p.z + normal.z * adjustedHalfWidth;

        positions[(idx + 1) * 3] = p.x - normal.x * adjustedHalfWidth;
        positions[(idx + 1) * 3 + 1] = p.y - normal.y * adjustedHalfWidth;
        positions[(idx + 1) * 3 + 2] = p.z - normal.z * adjustedHalfWidth;

        opacities[idx] = baseOpacity;
        opacities[idx + 1] = baseOpacity;
      }

      const posAttr = geometry.attributes["position"] as BufferAttribute;
      const opacityAttr = geometry.attributes["aOpacity"] as BufferAttribute;
      posAttr.needsUpdate = true;
      opacityAttr.needsUpdate = true;

      material.uniforms["uIntensity"]!.value =
        intensity * this.globalIntensity * this.intensityMultiplier;
      mesh.visible = true;
    }

    pooledArc.active = true;
  }

  private hideArc(pooledArc: PooledArc): void {
    for (const layer of pooledArc.layers) {
      layer.mesh.visible = false;
    }
    pooledArc.active = false;
  }

  /**
   * Update arcs (manual mode - just animates existing arcs without spawning)
   * Call this when using spawnArc() manually
   */
  public update(deltaTime: number = 1 / 60): void {
    // Hide all pooled arcs first
    for (const pooledArc of this.arcPool) {
      this.hideArc(pooledArc);
    }

    let poolIndex = 0;
    this.arcs = this.arcs.filter((arc) => {
      arc.progress += deltaTime / arc.lifetime;
      if (arc.progress >= 1) return false;

      const curvePoints = this.generateArcCurve(arc);
      const arcLength = arc.startPoint.distanceTo(arc.endPoint);

      // Fade in/out
      const fade =
        arc.progress < 0.2
          ? arc.progress / 0.2
          : arc.progress > 0.7
            ? (1 - arc.progress) / 0.3
            : 1;

      const intensity = Math.min(1, fade * arc.baseIntensity * 0.8);

      if (intensity > 0.01 && poolIndex < this.arcPool.length) {
        const pooledArc = this.arcPool[poolIndex];
        if (pooledArc) {
          this.updatePooledArc(pooledArc, curvePoints, intensity, arcLength);
          poolIndex++;
        }
      }

      return true;
    });
  }

  public dispose(): void {
    for (const pooledArc of this.arcPool) {
      for (const layer of pooledArc.layers) {
        layer.geometry.dispose();
        layer.material.dispose();
        this.remove(layer.mesh);
      }
    }
    this.arcPool = [];
    this.arcs = [];
  }
}
